import React, { useState, useEffect } from 'react';
import { Save, Eye, Download, RefreshCw, Palette, Upload, Image as ImageIcon, Video, Trash2, ExternalLink, Settings as SettingsIcon, Type, Film } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const HotspotCustomizer = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState('branding');

  const [settings, setSettings] = useState({
    primary_color: '#f97316',
    secondary_color: '#ea580c',
    company_name: '',
    company_tagline: 'Fast & Reliable Internet Connection',
    company_phone: '08888200888',
    company_location: 'Depok',
    logo_url: '',
    logo_size: 120,
    company_name_size: 28,
    tagline_size: 13,
    welcome_text: 'Selamat Datang!',
    instruction_text: 'Pilih metode login untuk terhubung ke internet',
    help_text: 'Butuh bantuan atau voucher internet?',
    login_button_text: 'MASUK',
    voucher_button_text: 'GUNAKAN VOUCHER',
    username_label: 'Username',
    username_placeholder: 'Masukkan username',
    password_label: 'Password',
    password_placeholder: 'Masukkan password',
    voucher_label: 'Kode Voucher',
    voucher_placeholder: 'Masukkan kode voucher',
    voucher_help_text: 'Masukkan kode voucher yang Anda terima',
    slider_interval: 5,
    idle_timeout: 10,
    slider_images: [
      'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1920&h=1080&fit=crop',
      'https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=1920&h=1080&fit=crop',
      'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=1920&h=1080&fit=crop'
    ],
    slider_video_url: 'https://customer-assets.emergentagent.com/job_netcenter-isp/artifacts/24b7k8wz_film.mp4'
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v2/hotspot/settings`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setSettings(data.data);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Gagal memuat settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v2/hotspot/settings`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Settings berhasil disimpan dan halaman hotspot diperbarui!');
      } else {
        toast.error(data.message || 'Gagal menyimpan settings');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Gagal menyimpan settings');
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (file, fileType) => {
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('file_type', fileType);

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v2/hotspot/upload?file_type=${fileType}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        toast.success(`${fileType === 'logo' ? 'Logo' : fileType === 'video' ? 'Video' : 'Gambar'} berhasil diupload!`);
        return data.data.url;
      } else {
        toast.error(data.message || 'Upload gagal');
        return null;
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Gagal upload file');
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const url = await handleFileUpload(file, 'logo');
      if (url) {
        setSettings(prev => ({ ...prev, logo_url: url }));
      }
    }
  };

  const handleSliderImageUpload = async (e, index) => {
    const file = e.target.files[0];
    if (file) {
      const url = await handleFileUpload(file, 'image');
      if (url) {
        handleImageChange(index, url);
      }
    }
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const url = await handleFileUpload(file, 'video');
      if (url) {
        setSettings(prev => ({ ...prev, slider_video_url: url }));
      }
    }
  };

  const handleAddImage = () => {
    setSettings(prev => ({
      ...prev,
      slider_images: [...prev.slider_images, '']
    }));
  };

  const handleRemoveImage = (index) => {
    setSettings(prev => ({
      ...prev,
      slider_images: prev.slider_images.filter((_, i) => i !== index)
    }));
  };

  const handleImageChange = (index, value) => {
    setSettings(prev => ({
      ...prev,
      slider_images: prev.slider_images.map((img, i) => i === index ? value : img)
    }));
  };

  const openPreview = () => {
    window.open('/hotspot_login.html', '_blank');
  };

  const downloadHotspotHTML = () => {
    const link = document.createElement('a');
    link.href = '/hotspot_login.html';
    link.download = 'hotspot_login.html';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('File hotspot_login.html berhasil didownload!');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  const tabs = [
    { id: 'branding', label: 'Branding', icon: Palette },
    { id: 'text', label: 'Custom Text', icon: Type },
    { id: 'media', label: 'Media', icon: Film },
    { id: 'settings', label: 'Settings', icon: SettingsIcon }
  ];

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      {/* Compact Header */}
      <div className="mb-6 bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl p-6 text-white shadow-lg">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2">
              <Palette className="h-7 w-7" />
              Hotspot Customizer
            </h1>
            <p className="mt-1 text-orange-100 text-sm">
              Customize halaman login hotspot MikroTik dengan mudah
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={downloadHotspotHTML}
              className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors shadow-md"
            >
              <Download className="h-4 w-4" />
              <span className="hidden sm:inline">Download</span>
            </button>
            <button
              onClick={openPreview}
              className="flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur text-white rounded-lg hover:bg-white/30 transition-colors"
            >
              <Eye className="h-4 w-4" />
              <span className="hidden sm:inline">Preview</span>
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-5 py-2 bg-white text-orange-600 rounded-lg hover:bg-orange-50 transition-all font-semibold shadow-md disabled:opacity-50 dark:bg-slate-800 dark:text-orange-400 dark:hover:bg-orange-900/30"
            >
              {saving ? (
                <>
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  <span className="hidden sm:inline">Saving...</span>
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  <span className="hidden sm:inline">Simpan</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="mb-6 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl shadow-sm border border-gray-200 overflow-hidden dark:border-slate-700">
        <div className="flex overflow-x-auto">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 min-w-[100px] flex items-center justify-center gap-2 px-3 py-2.5 text-sm font-semibold transition-all duration-200 ${
                  activeTab === tab.id
                    ? 'bg-gradient-to-br from-orange-400 via-orange-500 to-orange-600 text-white shadow-lg scale-105'
                    : 'bg-transparent text-gray-600 hover:bg-white hover:text-orange-600 hover:shadow-sm'
                }`}
              >
                <Icon className="h-4 w-4" />
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 dark:bg-slate-800 dark:border-slate-700">
        {activeTab === 'branding' && (
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2 pb-3 border-b dark:text-slate-100">
              <Palette className="h-6 w-6 text-orange-500" />
              Branding & Warna
            </h2>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Colors */}
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-800 dark:text-slate-200">Warna Tema</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">Primary</label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={settings.primary_color}
                        onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })}
                        className="h-10 w-16 rounded cursor-pointer border"
                      />
                      <input
                        type="text"
                        value={settings.primary_color}
                        onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })}
                        className="flex-1 px-3 py-2 text-sm border rounded focus:ring-2 focus:ring-orange-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">Secondary</label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={settings.secondary_color}
                        onChange={(e) => setSettings({ ...settings, secondary_color: e.target.value })}
                        className="h-10 w-16 rounded cursor-pointer border"
                      />
                      <input
                        type="text"
                        value={settings.secondary_color}
                        onChange={(e) => setSettings({ ...settings, secondary_color: e.target.value })}
                        className="flex-1 px-3 py-2 text-sm border rounded focus:ring-2 focus:ring-orange-500"
                      />
                    </div>
                  </div>
                </div>
                {/* Preview Gradient */}
                <div className="h-16 rounded-lg" style={{
                  background: `linear-gradient(135deg, ${settings.primary_color} 0%, ${settings.secondary_color} 100%)`
                }}>
                  <div className="h-full flex items-center justify-center">
                    <span className="text-white font-semibold">Preview Gradient</span>
                  </div>
                </div>
              </div>

              {/* Logo */}
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-800 dark:text-slate-200">Logo Perusahaan</h3>
                {settings.logo_url ? (
                  <div className="p-4 bg-gray-50 rounded-lg border dark:bg-slate-900">
                    <div className="flex justify-between items-center mb-3">
                      <span className="text-sm font-medium text-gray-700 dark:text-slate-200">Logo Aktif</span>
                      <button
                        onClick={() => setSettings({ ...settings, logo_url: '' })}
                        className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1"
                      >
                        <Trash2 className="h-3 w-3" />
                        Hapus
                      </button>
                    </div>
                    <img
                      src={settings.logo_url}
                      alt="Logo"
                      className="h-20 w-auto mx-auto object-contain bg-white p-2 rounded border dark:bg-slate-800"
                    />
                    <label className="mt-3 block text-center">
                      <span className="text-xs text-blue-600 hover:text-blue-700 cursor-pointer dark:text-blue-400">
                        {uploading ? 'Uploading...' : 'Ganti Logo'}
                      </span>
                      <input
                        type="file"
                        accept=".png,.jpg,.jpeg,.svg"
                        onChange={handleLogoUpload}
                        className="hidden"
                        disabled={uploading}
                      />
                    </label>
                  </div>
                ) : (
                  <label className="block cursor-pointer">
                    <div className="border-2 border-dashed border-orange-300 rounded-lg p-6 text-center hover:bg-orange-50 transition dark:hover:bg-orange-900/30">
                      <Upload className="h-8 w-8 text-orange-500 mx-auto mb-2" />
                      <span className="text-sm font-medium text-orange-700 dark:text-orange-300">
                        {uploading ? 'Uploading...' : 'Upload Logo'}
                      </span>
                      <p className="text-xs text-gray-500 mt-1 dark:text-slate-400">PNG, JPG, SVG • Max 2MB</p>
                    </div>
                    <input
                      type="file"
                      accept=".png,.jpg,.jpeg,.svg"
                      onChange={handleLogoUpload}
                      className="hidden"
                      disabled={uploading}
                    />
                  </label>
                )}
              </div>
            </div>

            {/* Company Info */}
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-800 dark:text-slate-200">Informasi Perusahaan</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-slate-200">Nama Perusahaan</label>
                  <input
                    type="text"
                    value={settings.company_name}
                    onChange={(e) => setSettings({ ...settings, company_name: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-slate-200">Tagline</label>
                  <input
                    type="text"
                    value={settings.company_tagline}
                    onChange={(e) => setSettings({ ...settings, company_tagline: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-slate-200">Nomor Telepon</label>
                  <input
                    type="text"
                    value={settings.company_phone}
                    onChange={(e) => setSettings({ ...settings, company_phone: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-slate-200">Lokasi</label>
                  <input
                    type="text"
                    value={settings.company_location}
                    onChange={(e) => setSettings({ ...settings, company_location: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                  />
                </div>
              </div>
            </div>

            {/* Sizes */}
            <div className="space-y-4 p-4 bg-blue-50 rounded-lg dark:bg-blue-900/30">
              <h3 className="font-semibold text-gray-800 dark:text-slate-200">Ukuran Elemen</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <div className="flex justify-between mb-1">
                    <label className="text-sm font-medium text-gray-700 dark:text-slate-200">Logo</label>
                    <span className="text-sm text-gray-600 font-mono dark:text-slate-300">{settings.logo_size}px</span>
                  </div>
                  <input
                    type="range"
                    min="60"
                    max="300"
                    value={settings.logo_size}
                    onChange={(e) => setSettings({ ...settings, logo_size: parseInt(e.target.value) })}
                    className="w-full"
                  />
                </div>
                <div>
                  <div className="flex justify-between mb-1">
                    <label className="text-sm font-medium text-gray-700 dark:text-slate-200">Nama</label>
                    <span className="text-sm text-gray-600 font-mono dark:text-slate-300">{settings.company_name_size}px</span>
                  </div>
                  <input
                    type="range"
                    min="20"
                    max="48"
                    value={settings.company_name_size}
                    onChange={(e) => setSettings({ ...settings, company_name_size: parseInt(e.target.value) })}
                    className="w-full"
                  />
                </div>
                <div>
                  <div className="flex justify-between mb-1">
                    <label className="text-sm font-medium text-gray-700 dark:text-slate-200">Tagline</label>
                    <span className="text-sm text-gray-600 font-mono dark:text-slate-300">{settings.tagline_size}px</span>
                  </div>
                  <input
                    type="range"
                    min="10"
                    max="20"
                    value={settings.tagline_size}
                    onChange={(e) => setSettings({ ...settings, tagline_size: parseInt(e.target.value) })}
                    className="w-full"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'text' && (
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2 pb-3 border-b dark:text-slate-100">
              <Type className="h-6 w-6 text-orange-500" />
              Custom Text & Label
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-slate-200">Welcome Text</label>
                <input
                  type="text"
                  value={settings.welcome_text}
                  onChange={(e) => setSettings({ ...settings, welcome_text: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-slate-200">Instruction Text</label>
                <input
                  type="text"
                  value={settings.instruction_text}
                  onChange={(e) => setSettings({ ...settings, instruction_text: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-slate-200">Help Text</label>
                <input
                  type="text"
                  value={settings.help_text}
                  onChange={(e) => setSettings({ ...settings, help_text: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-slate-200">Username Label</label>
                <input
                  type="text"
                  value={settings.username_label}
                  onChange={(e) => setSettings({ ...settings, username_label: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-slate-200">Username Placeholder</label>
                <input
                  type="text"
                  value={settings.username_placeholder}
                  onChange={(e) => setSettings({ ...settings, username_placeholder: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-slate-200">Password Label</label>
                <input
                  type="text"
                  value={settings.password_label}
                  onChange={(e) => setSettings({ ...settings, password_label: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-slate-200">Password Placeholder</label>
                <input
                  type="text"
                  value={settings.password_placeholder}
                  onChange={(e) => setSettings({ ...settings, password_placeholder: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-slate-200">Voucher Label</label>
                <input
                  type="text"
                  value={settings.voucher_label}
                  onChange={(e) => setSettings({ ...settings, voucher_label: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-slate-200">Voucher Placeholder</label>
                <input
                  type="text"
                  value={settings.voucher_placeholder}
                  onChange={(e) => setSettings({ ...settings, voucher_placeholder: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-slate-200">Voucher Help Text</label>
                <input
                  type="text"
                  value={settings.voucher_help_text}
                  onChange={(e) => setSettings({ ...settings, voucher_help_text: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-slate-200">Login Button Text</label>
                <input
                  type="text"
                  value={settings.login_button_text}
                  onChange={(e) => setSettings({ ...settings, login_button_text: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-slate-200">Voucher Button Text</label>
                <input
                  type="text"
                  value={settings.voucher_button_text}
                  onChange={(e) => setSettings({ ...settings, voucher_button_text: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                />
              </div>
            </div>
          </div>
        )}

        {activeTab === 'media' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between pb-3 border-b">
              <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2 dark:text-slate-100">
                <Film className="h-6 w-6 text-orange-500" />
                Slider & Video
              </h2>
              <button
                onClick={handleAddImage}
                className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 text-sm"
              >
                <ImageIcon className="h-4 w-4" />
                Tambah Gambar
              </button>
            </div>

            {/* Slider Images */}
            <div className="space-y-3">
              {settings.slider_images.map((image, index) => (
                <div key={index} className="border rounded-lg p-4 bg-gray-50 dark:bg-slate-900">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-semibold text-gray-700 dark:text-slate-200">Gambar {index + 1}</span>
                    {settings.slider_images.length > 1 && (
                      <button
                        onClick={() => handleRemoveImage(index)}
                        className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1"
                      >
                        <Trash2 className="h-3 w-3" />
                        Hapus
                      </button>
                    )}
                  </div>
                  {image ? (
                    <div className="space-y-2">
                      <img
                        src={image}
                        alt={`Slider ${index + 1}`}
                        className="w-full h-32 object-cover rounded"
                      />
                      <label className="block text-center">
                        <span className="text-xs text-blue-600 hover:text-blue-700 cursor-pointer dark:text-blue-400">
                          {uploading ? 'Uploading...' : 'Ganti Gambar'}
                        </span>
                        <input
                          type="file"
                          accept=".png,.jpg,.jpeg,.webp"
                          onChange={(e) => handleSliderImageUpload(e, index)}
                          className="hidden"
                          disabled={uploading}
                        />
                      </label>
                    </div>
                  ) : (
                    <label className="block cursor-pointer">
                      <div className="border-2 border-dashed border-blue-300 rounded-lg p-4 text-center hover:bg-blue-50 dark:hover:bg-blue-900/30">
                        <Upload className="h-6 w-6 text-blue-500 mx-auto mb-1" />
                        <span className="text-sm text-blue-700 dark:text-blue-300">{uploading ? 'Uploading...' : 'Upload'}</span>
                      </div>
                      <input
                        type="file"
                        accept=".png,.jpg,.jpeg,.webp"
                        onChange={(e) => handleSliderImageUpload(e, index)}
                        className="hidden"
                        disabled={uploading}
                      />
                    </label>
                  )}
                </div>
              ))}
            </div>

            {/* Video */}
            <div className="border rounded-lg p-4 bg-purple-50 dark:bg-purple-900/30">
              <h3 className="font-semibold text-gray-800 mb-3 dark:text-slate-200">Video Slider (Opsional)</h3>
              {settings.slider_video_url ? (
                <div className="space-y-2">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-slate-200">Video Aktif</span>
                    <button
                      onClick={() => setSettings({ ...settings, slider_video_url: '' })}
                      className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1"
                    >
                      <Trash2 className="h-3 w-3" />
                      Hapus
                    </button>
                  </div>
                  <video
                    src={settings.slider_video_url}
                    className="w-full max-h-48 rounded border bg-black"
                    controls
                  />
                  <label className="block text-center">
                    <span className="text-xs text-purple-600 hover:text-purple-700 cursor-pointer dark:text-purple-400">
                      {uploading ? 'Uploading...' : 'Ganti Video'}
                    </span>
                    <input
                      type="file"
                      accept=".mp4,.webm"
                      onChange={handleVideoUpload}
                      className="hidden"
                      disabled={uploading}
                    />
                  </label>
                </div>
              ) : (
                <label className="block cursor-pointer">
                  <div className="border-2 border-dashed border-purple-300 rounded-lg p-6 text-center hover:bg-purple-100 dark:hover:bg-purple-900/40">
                    <Video className="h-8 w-8 text-purple-500 mx-auto mb-2" />
                    <span className="text-sm text-purple-700 dark:text-purple-300">{uploading ? 'Uploading...' : 'Upload Video'}</span>
                    <p className="text-xs text-purple-600 mt-1 dark:text-purple-400">MP4, WebM • Max 50MB</p>
                  </div>
                  <input
                    type="file"
                    accept=".mp4,.webm"
                    onChange={handleVideoUpload}
                    className="hidden"
                    disabled={uploading}
                  />
                </label>
              )}
            </div>
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2 pb-3 border-b dark:text-slate-100">
              <SettingsIcon className="h-6 w-6 text-orange-500" />
              Pengaturan Waktu
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-4 bg-gray-50 rounded-lg dark:bg-slate-900">
                <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
                  Interval Slider (detik)
                </label>
                <input
                  type="number"
                  min="3"
                  max="15"
                  value={settings.slider_interval}
                  onChange={(e) => setSettings({ ...settings, slider_interval: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-slate-400">Gambar berganti setiap berapa detik (3-15)</p>
              </div>

              <div className="p-4 bg-gray-50 rounded-lg dark:bg-slate-900">
                <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
                  Idle Timeout (detik)
                </label>
                <input
                  type="number"
                  min="5"
                  max="60"
                  value={settings.idle_timeout}
                  onChange={(e) => setSettings({ ...settings, idle_timeout: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-slate-400">Waktu idle sebelum fullscreen (5-60)</p>
              </div>
            </div>

            {/* Instructions */}
            <div className="bg-gradient-to-r from-orange-50 to-orange-100 rounded-lg border border-orange-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3 dark:text-slate-100">📋 Cara Upload ke MikroTik</h3>
              <ol className="space-y-2 text-sm text-gray-700 dark:text-slate-200">
                <li className="flex gap-2">
                  <span className="font-semibold text-orange-600 dark:text-orange-400">1.</span>
                  <span>Klik <strong>"Simpan & Terapkan"</strong> untuk generate HTML</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold text-orange-600 dark:text-orange-400">2.</span>
                  <span>Klik tombol hijau <strong>"Download HTML"</strong></span>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold text-orange-600 dark:text-orange-400">3.</span>
                  <span>Login ke MikroTik (WinBox atau WebFig)</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold text-orange-600 dark:text-orange-400">4.</span>
                  <span>Files → Upload <code>hotspot_login.html</code></span>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold text-orange-600 dark:text-orange-400">5.</span>
                  <span>IP → Hotspot → Server Profiles → Set login page</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold text-orange-600 dark:text-orange-400">6.</span>
                  <span><strong>Done!</strong> Test dengan connect ke WiFi</span>
                </li>
              </ol>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HotspotCustomizer;
